"use client";

import { useEffect, useRef } from "react";
import * as THREE from "three";

type ScanStage = "APPROACHING" | "SCANNING" | "DETECTED";

/**
 * Smooth minimum (polynomial smin) para unir formas orgânicas suavemente
 */
function smin(a: number, b: number, k: number): number {
  const h = Math.max(k - Math.abs(a - b), 0) / k;
  return Math.min(a, b) - h * h * k * 0.25;
}

/**
 * Distância aproximada até uma elipse rotacionada
 */
function sdRotatedEllipse(
  px: number,
  py: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  angle: number,
): number {
  const dx = px - cx;
  const dy = py - cy;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const lx = dx * cos + dy * sin;
  const ly = -dx * sin + dy * cos;
  const norm = Math.sqrt((lx * lx) / (rx * rx) + (ly * ly) / (ry * ry));
  return (norm - 1) * Math.min(rx, ry);
}

/**
 * SDF da almofada principal (formato clássico da patinha)
 */
function sdMainPad(x: number, y: number): number {
  const dTop = Math.hypot(x - 0, y - -0.19) - 0.21;
  const dLeft = Math.hypot(x - -0.165, y - -0.39) - 0.195;
  const dRight = Math.hypot(x - 0.165, y - -0.39) - 0.195;
  const dBottomCenter = Math.hypot(x - 0, y - -0.36) - 0.205;
  return smin(smin(dLeft, dRight, 0.14), smin(dTop, dBottomCenter, 0.14), 0.12);
}

/**
 * SDF combinando os 4 dedinhos + almofada central para gerar a silhueta externa recortada
 */
function sdOuterPaw(x: number, y: number): number {
  const t1 = sdRotatedEllipse(x, y, -0.45, 0.06, 0.16, 0.245, 0.38);
  const t2 = sdRotatedEllipse(x, y, -0.165, 0.33, 0.16, 0.26, 0.12);
  const t3 = sdRotatedEllipse(x, y, 0.165, 0.33, 0.16, 0.26, -0.12);
  const t4 = sdRotatedEllipse(x, y, 0.45, 0.06, 0.16, 0.245, -0.38);
  const pad = sdMainPad(x, y);

  const toes = smin(smin(t1, t2, 0.12), smin(t3, t4, 0.12), 0.12);
  const combined = smin(toes, pad, 0.15);
  return combined - 0.11;
}

/**
 * Traça um contorno fechado suave (sem auto-interseções) a partir de uma SDF estrelada
 */
function traceStarContour(
  sdf: (x: number, y: number) => number,
  ox: number,
  oy: number,
  samples: number,
): THREE.Vector2[] {
  const pts: THREE.Vector2[] = [];
  for (let i = 0; i < samples; i++) {
    const theta = (i / samples) * Math.PI * 2;
    const dx = Math.cos(theta);
    const dy = Math.sin(theta);

    let lo = 0.02;
    let hi = 1.4;
    for (let iter = 0; iter < 22; iter++) {
      const mid = (lo + hi) * 0.5;
      if (sdf(ox + dx * mid, oy + dy * mid) <= 0) {
        lo = mid;
      } else {
        hi = mid;
      }
    }
    const r = (lo + hi) * 0.5;
    pts.push(new THREE.Vector2(ox + dx * r, oy + dy * r));
  }
  return pts;
}

/**
 * Cria um Shape sólido de elipse rotacionada (sem furos -> zero erros de triangulação)
 */
function createRotatedEllipseShape(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  angle: number,
  segments = 48,
): THREE.Shape {
  const pts: THREE.Vector2[] = [];
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  for (let i = 0; i < segments; i++) {
    const t = (i / segments) * Math.PI * 2;
    const lx = Math.cos(t) * rx;
    const ly = Math.sin(t) * ry;
    const x = cx + lx * cos - ly * sin;
    const y = cy + lx * sin + ly * cos;
    pts.push(new THREE.Vector2(x, y));
  }
  return new THREE.Shape(pts);
}

/**
 * Cria um arco espesso com pontas arredondadas (para as 3 ondas do símbolo NFC em 3D)
 */
function createArcWaveShape(
  cx: number,
  cy: number,
  radius: number,
  thickness: number,
  startAngle: number,
  endAngle: number,
): THREE.Shape {
  const shape = new THREE.Shape();
  const halfT = thickness * 0.5;
  const rInner = radius - halfT;
  const rOuter = radius + halfT;

  shape.absarc(cx, cy, rOuter, startAngle, endAngle, false);
  const endCapX = cx + Math.cos(endAngle) * radius;
  const endCapY = cy + Math.sin(endAngle) * radius;
  shape.absarc(endCapX, endCapY, halfT, endAngle, endAngle + Math.PI, false);
  shape.absarc(cx, cy, rInner, endAngle, startAngle, true);
  const startCapX = cx + Math.cos(startAngle) * radius;
  const startCapY = cy + Math.sin(startAngle) * radius;
  shape.absarc(
    startCapX,
    startCapY,
    halfT,
    startAngle + Math.PI,
    startAngle,
    false,
  );
  shape.closePath();
  return shape;
}

/**
 * Constrói os Shapes das letras "N", "F", "C" em relevo 3D dentro da patinha
 */
function createNFCLetterShapes(
  centerX: number,
  centerY: number,
  scale = 1,
): THREE.Shape[] {
  const shapes: THREE.Shape[] = [];
  const s = scale;

  // Letra N
  const nx = centerX - 0.135 * s;
  const ny = centerY;
  const nShape = new THREE.Shape();
  nShape.moveTo(nx - 0.042 * s, ny - 0.055 * s);
  nShape.lineTo(nx - 0.042 * s, ny + 0.055 * s);
  nShape.lineTo(nx - 0.015 * s, ny + 0.055 * s);
  nShape.lineTo(nx + 0.018 * s, ny - 0.012 * s);
  nShape.lineTo(nx + 0.018 * s, ny + 0.055 * s);
  nShape.lineTo(nx + 0.044 * s, ny + 0.055 * s);
  nShape.lineTo(nx + 0.044 * s, ny - 0.055 * s);
  nShape.lineTo(nx + 0.017 * s, ny - 0.055 * s);
  nShape.lineTo(nx - 0.016 * s, ny + 0.012 * s);
  nShape.lineTo(nx - 0.016 * s, ny - 0.055 * s);
  nShape.closePath();
  shapes.push(nShape);

  // Letra F
  const fx = centerX;
  const fy = centerY;
  const fShape = new THREE.Shape();
  fShape.moveTo(fx - 0.036 * s, fy - 0.055 * s);
  fShape.lineTo(fx - 0.036 * s, fy + 0.055 * s);
  fShape.lineTo(fx + 0.038 * s, fy + 0.055 * s);
  fShape.lineTo(fx + 0.038 * s, fy + 0.031 * s);
  fShape.lineTo(fx - 0.01 * s, fy + 0.031 * s);
  fShape.lineTo(fx - 0.01 * s, fy + 0.008 * s);
  fShape.lineTo(fx + 0.03 * s, fy + 0.008 * s);
  fShape.lineTo(fx + 0.03 * s, fy - 0.015 * s);
  fShape.lineTo(fx - 0.01 * s, fy - 0.015 * s);
  fShape.lineTo(fx - 0.01 * s, fy - 0.055 * s);
  fShape.closePath();
  shapes.push(fShape);

  // Letra C
  const cx = centerX + 0.125 * s;
  const cy = centerY;
  const cShape = createArcWaveShape(
    cx,
    cy,
    0.042 * s,
    0.025 * s,
    Math.PI * 0.28,
    Math.PI * 1.72,
  );
  shapes.push(cShape);

  return shapes;
}

/**
 * Constrói a Tag 3D em formato de Patinha Recortada sem falhas de triangulação
 */
function buildPawShapedTag3D(): THREE.Group {
  const tagGroup = new THREE.Group();

  const orangeMat = new THREE.MeshStandardMaterial({
    color: 0xf97316,
    roughness: 0.35,
    metalness: 0.06,
  });

  const whiteMat = new THREE.MeshStandardMaterial({
    color: 0xf8fafc,
    roughness: 0.28,
    metalness: 0.04,
  });

  // 1. Contorno externo recortado da patinha
  const outerPoints = traceStarContour(sdOuterPaw, 0, -0.04, 180);
  const pawOuterShape = new THREE.Shape(outerPoints);

  // Camada Traseira Laranja
  const backPlateGeom = new THREE.ExtrudeGeometry(pawOuterShape, {
    depth: 0.06,
    bevelEnabled: true,
    bevelThickness: 0.02,
    bevelSize: 0.018,
    bevelSegments: 4,
  });
  const backPlateMesh = new THREE.Mesh(backPlateGeom, orangeMat);
  backPlateMesh.position.set(0, 0, -0.06);
  tagGroup.add(backPlateMesh);

  // Orelhinha superior Laranja com furo para a argola (mais alta para ficar bem visível abaixo da coleira)
  const tabShape = new THREE.Shape();
  tabShape.absarc(0, 0.68, 0.2, 0, Math.PI * 2, false);
  const tabHole = new THREE.Path();
  tabHole.absarc(0, 0.72, 0.08, 0, Math.PI * 2, true);
  tabShape.holes.push(tabHole);

  const tabGeom = new THREE.ExtrudeGeometry(tabShape, {
    depth: 0.08,
    bevelEnabled: true,
    bevelThickness: 0.016,
    bevelSize: 0.014,
    bevelSegments: 4,
  });
  const tabMesh = new THREE.Mesh(tabGeom, orangeMat);
  tabMesh.position.set(0, 0, -0.04);
  tagGroup.add(tabMesh);

  // Camada Frontal Branca
  const whiteBodyGeom = new THREE.ExtrudeGeometry(pawOuterShape, {
    depth: 0.055,
    bevelEnabled: true,
    bevelThickness: 0.016,
    bevelSize: 0.012,
    bevelSegments: 4,
  });
  const whiteBodyMesh = new THREE.Mesh(whiteBodyGeom, whiteMat);
  whiteBodyMesh.position.set(0, 0, 0.01);
  tagGroup.add(whiteBodyMesh);

  // 2. Os 4 dedinhos ovais Laranjas + Almofada central Laranja
  const toeShapes: THREE.Shape[] = [
    createRotatedEllipseShape(-0.45, 0.06, 0.155, 0.24, -0.38),
    createRotatedEllipseShape(-0.165, 0.33, 0.155, 0.255, -0.12),
    createRotatedEllipseShape(0.165, 0.33, 0.155, 0.255, 0.12),
    createRotatedEllipseShape(0.45, 0.06, 0.155, 0.24, 0.38),
  ];

  const mainPadPts = traceStarContour(sdMainPad, 0, -0.32, 96);
  const mainPadShape = new THREE.Shape(mainPadPts);

  const padsGeom = new THREE.ExtrudeGeometry([...toeShapes, mainPadShape], {
    depth: 0.02,
    bevelEnabled: true,
    bevelThickness: 0.006,
    bevelSize: 0.005,
    bevelSegments: 3,
  });
  const padsMesh = new THREE.Mesh(padsGeom, orangeMat);
  padsMesh.position.set(0, 0, 0.065);
  tagGroup.add(padsMesh);

  // 3. Símbolo NFC (3 ondas + letras "NFC") em Alto-Relevo Branco
  const nfcDetailShapes: THREE.Shape[] = [
    createArcWaveShape(0, -0.295, 0.082, 0.022, Math.PI * 0.25, Math.PI * 0.75),
    createArcWaveShape(0, -0.295, 0.14, 0.022, Math.PI * 0.23, Math.PI * 0.77),
    createArcWaveShape(0, -0.295, 0.198, 0.022, Math.PI * 0.21, Math.PI * 0.79),
    ...createNFCLetterShapes(0, -0.4, 0.9),
  ];

  const nfcDetailGeom = new THREE.ExtrudeGeometry(nfcDetailShapes, {
    depth: 0.028,
    bevelEnabled: true,
    bevelThickness: 0.006,
    bevelSize: 0.005,
    bevelSegments: 3,
  });
  const nfcDetailMesh = new THREE.Mesh(nfcDetailGeom, whiteMat);
  nfcDetailMesh.position.set(0, 0, 0.082);
  tagGroup.add(nfcDetailMesh);

  return tagGroup;
}

/**
 * Gera um perfil 2D de retângulo com cantos arredondados para uso com THREE.LatheGeometry
 */
function createRoundedRectLatheProfile(
  rInner: number,
  rOuter: number,
  yBottom: number,
  yTop: number,
  cornerRadius: number,
  cornerSegments = 8,
): THREE.Vector2[] {
  const pts: THREE.Vector2[] = [];
  const cr = Math.min(
    cornerRadius,
    (rOuter - rInner) * 0.45,
    (yTop - yBottom) * 0.45,
  );

  const corners = [
    {
      cx: rOuter - cr,
      cy: yBottom + cr,
      start: -Math.PI * 0.5,
      end: 0,
    },
    {
      cx: rOuter - cr,
      cy: yTop - cr,
      start: 0,
      end: Math.PI * 0.5,
    },
    {
      cx: rInner + cr,
      cy: yTop - cr,
      start: Math.PI * 0.5,
      end: Math.PI,
    },
    {
      cx: rInner + cr,
      cy: yBottom + cr,
      start: Math.PI,
      end: Math.PI * 1.5,
    },
  ];

  for (const c of corners) {
    for (let i = 0; i <= cornerSegments; i++) {
      const a = c.start + ((c.end - c.start) * i) / cornerSegments;
      pts.push(
        new THREE.Vector2(c.cx + Math.cos(a) * cr, c.cy + Math.sin(a) * cr),
      );
    }
  }
  pts.push(pts[0].clone());
  return pts;
}

/**
 * Constrói a Coleira 3D usando LatheGeometry em coordenadas cilíndricas exatas
 */
function buildStylizedCollar3D(): THREE.Group {
  const collarGroup = new THREE.Group();

  const strapMat = new THREE.MeshStandardMaterial({
    color: 0xea580c,
    roughness: 0.5,
    metalness: 0.05,
  });

  const creamMat = new THREE.MeshStandardMaterial({
    color: 0xfef3c7,
    roughness: 0.38,
    metalness: 0.08,
  });

  const keeperMat = new THREE.MeshStandardMaterial({
    color: 0xfbbf24,
    roughness: 0.4,
    metalness: 0.08,
  });

  // 1. Anel principal da coleira
  const mainProfile = createRoundedRectLatheProfile(
    1.36,
    1.66,
    -0.36,
    0.36,
    0.11,
  );
  const mainBandGeom = new THREE.LatheGeometry(mainProfile, 96);
  const mainBandMesh = new THREE.Mesh(mainBandGeom, strapMat);
  collarGroup.add(mainBandMesh);

  // 2. Tira frontal sobreposta
  const strapStartPhi = -0.46;
  const strapLengthPhi = 0.72;
  const frontStrapProfile = createRoundedRectLatheProfile(
    1.62,
    1.82,
    -0.21,
    0.21,
    0.08,
  );
  const frontStrapGeom = new THREE.LatheGeometry(
    frontStrapProfile,
    48,
    strapStartPhi,
    strapLengthPhi,
  );
  const frontStrapMesh = new THREE.Mesh(frontStrapGeom, strapMat);
  collarGroup.add(frontStrapMesh);

  // Ponta arredondada suave da tira frontal (esfera achatada na espessura exata da tira)
  const endPhi = strapStartPhi + strapLengthPhi;
  const tipRadius = 1.72;
  const tipCapGeom = new THREE.SphereGeometry(0.21, 28, 28);
  // Escala radial = 0.45 (espessura 0.19), escala Y = 1.0 (altura 0.42), escala tangencial = 0.9
  tipCapGeom.scale(0.9, 1.0, 0.45);
  const tipCapMesh = new THREE.Mesh(tipCapGeom, strapMat);
  tipCapMesh.position.set(
    Math.sin(endPhi) * tipRadius,
    0,
    Math.cos(endPhi) * tipRadius,
  );
  tipCapMesh.rotation.y = endPhi;
  collarGroup.add(tipCapMesh);

  // 3. Fivela Creme na Frente-Esquerda
  const bucklePhi = -0.46;
  const buckleGroup = new THREE.Group();
  const buckleR = 1.68;
  buckleGroup.position.set(
    Math.sin(bucklePhi) * buckleR,
    0,
    Math.cos(bucklePhi) * buckleR,
  );
  buckleGroup.rotation.y = bucklePhi;

  const bw = 0.28;
  const bh = 0.43;
  const br = 0.11;
  const buckleShape = new THREE.Shape();
  buckleShape.moveTo(-bw + br, -bh);
  buckleShape.lineTo(bw - br, -bh);
  buckleShape.quadraticCurveTo(bw, -bh, bw, -bh + br);
  buckleShape.lineTo(bw, bh - br);
  buckleShape.quadraticCurveTo(bw, bh, bw - br, bh);
  buckleShape.lineTo(-bw + br, bh);
  buckleShape.quadraticCurveTo(-bw, bh, -bw, bh - br);
  buckleShape.lineTo(-bw, -bh + br);
  buckleShape.quadraticCurveTo(-bw, -bh, -bw + br, -bh);

  const hw = 0.16;
  const hh = 0.29;
  const hr = 0.05;
  const buckleHole = new THREE.Path();
  buckleHole.moveTo(-hw + hr, -hh);
  buckleHole.lineTo(hw - hr, -hh);
  buckleHole.quadraticCurveTo(hw, -hh, hw, -hh + hr);
  buckleHole.lineTo(hw, hh - hr);
  buckleHole.quadraticCurveTo(hw, hh, hw - hr, hh);
  buckleHole.lineTo(-hw + hr, hh);
  buckleHole.quadraticCurveTo(-hw, hh, -hw, hh - hr);
  buckleHole.lineTo(-hw, -hh + hr);
  buckleHole.quadraticCurveTo(-hw, -hh, -hw + hr, -hh);
  buckleShape.holes.push(buckleHole);

  const buckleGeom = new THREE.ExtrudeGeometry(buckleShape, {
    depth: 0.16,
    bevelEnabled: true,
    bevelThickness: 0.04,
    bevelSize: 0.035,
    bevelSegments: 5,
  });
  buckleGeom.center();
  const buckleMesh = new THREE.Mesh(buckleGeom, creamMat);
  buckleGroup.add(buckleMesh);

  const pinGeom = new THREE.CapsuleGeometry(0.058, 0.22, 12, 20);
  const pinMesh = new THREE.Mesh(pinGeom, creamMat);
  pinMesh.position.set(-0.22, 0, 0.08);
  pinMesh.rotation.z = Math.PI * 0.5;
  pinMesh.rotation.y = -0.25;
  buckleGroup.add(pinMesh);

  collarGroup.add(buckleGroup);

  // 4. Passador Amarelo/Âmbar (Keeper Loop) abraçando verticalmente a coleira + tira frontal
  const keeperPhi = -0.14;
  const keeperOuterShape = new THREE.Shape();
  const krIn = 1.31;
  const krOut = 1.9;
  const kyBot = -0.42;
  const kyTop = 0.42;
  const kcr = 0.11;
  keeperOuterShape.moveTo(krIn + kcr, kyBot);
  keeperOuterShape.lineTo(krOut - kcr, kyBot);
  keeperOuterShape.quadraticCurveTo(krOut, kyBot, krOut, kyBot + kcr);
  keeperOuterShape.lineTo(krOut, kyTop - kcr);
  keeperOuterShape.quadraticCurveTo(krOut, kyTop, krOut - kcr, kyTop);
  keeperOuterShape.lineTo(krIn + kcr, kyTop);
  keeperOuterShape.quadraticCurveTo(krIn, kyTop, krIn, kyTop - kcr);
  keeperOuterShape.lineTo(krIn, kyBot + kcr);
  keeperOuterShape.quadraticCurveTo(krIn, kyBot, krIn + kcr, kyBot);

  const keeperGeom = new THREE.ExtrudeGeometry(keeperOuterShape, {
    depth: 0.24,
    bevelEnabled: true,
    bevelThickness: 0.045,
    bevelSize: 0.04,
    bevelSegments: 5,
  });
  keeperGeom.translate(0, 0, -0.12);
  keeperGeom.rotateY(-Math.PI * 0.5);

  const keeperGroup = new THREE.Group();
  keeperGroup.rotation.y = keeperPhi;
  const keeperMesh = new THREE.Mesh(keeperGeom, keeperMat);
  keeperGroup.add(keeperMesh);

  // Argolinha creme presa bem na parte de baixo do passador amarelo (para a tag ficar pendurada abaixo da coleira!)
  const bailGeom = new THREE.TorusGeometry(0.14, 0.038, 16, 36);
  const bailMesh = new THREE.Mesh(bailGeom, creamMat);
  bailMesh.position.set(0, -0.52, 1.72);
  bailMesh.rotation.y = Math.PI * 0.5;
  keeperGroup.add(bailMesh);

  collarGroup.add(keeperGroup);

  // 5. Dois Rebites Redondos Creme na lateral direita da coleira
  const studAngles = [0.62, 0.98];
  for (const phi of studAngles) {
    const studGeom = new THREE.SphereGeometry(0.13, 24, 24);
    studGeom.scale(1, 1, 0.55);
    const studMesh = new THREE.Mesh(studGeom, creamMat);
    const sr = 1.66;
    studMesh.position.set(Math.sin(phi) * sr, 0, Math.cos(phi) * sr);
    studMesh.rotation.y = phi;
    collarGroup.add(studMesh);
  }

  collarGroup.scale.set(1.12, 1, 0.88);

  return collarGroup;
}

/**
 * Renderiza a tela dinâmica do smartphone de acordo com o estágio da leitura
 */
function drawPhoneScreen(
  ctx: CanvasRenderingContext2D,
  stage: ScanStage,
  pulse: number,
) {
  const w = 512;
  const h = 1024;

  const bg = ctx.createLinearGradient(0, 0, 0, h);
  bg.addColorStop(0, "#18181b");
  bg.addColorStop(1, "#09090b");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  // Dynamic Island
  ctx.fillStyle = "#000000";
  ctx.beginPath();
  ctx.roundRect(176, 26, 160, 36, 18);
  ctx.fill();

  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 22px system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("09:41", 44, 52);

  if (stage === "APPROACHING") {
    ctx.save();
    const glowRadius = 105 + Math.sin(pulse * 4) * 16;
    const glow = ctx.createRadialGradient(256, 270, 20, 256, 270, glowRadius);
    glow.addColorStop(0, "rgba(249, 115, 22, 0.5)");
    glow.addColorStop(1, "rgba(249, 115, 22, 0)");
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(256, 270, glowRadius, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = "#f97316";
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.arc(256, 270, 64, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = "#fbbf24";
    ctx.lineWidth = 7;
    ctx.lineCap = "round";
    for (let i = 1; i <= 3; i++) {
      ctx.beginPath();
      ctx.arc(256, 290, i * 16, -Math.PI * 0.75, -Math.PI * 0.25);
      ctx.stroke();
    }

    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 30px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Aproxime a Ponta do Celular", 256, 405);

    ctx.fillStyle = "#a1a1aa";
    ctx.font = "22px system-ui, sans-serif";
    ctx.fillText("Leitura NFC sem aplicativo", 256, 445);
    ctx.restore();
    return;
  }

  if (stage === "SCANNING") {
    const ringRadius = 85 + ((pulse * 60) % 85);
    ctx.strokeStyle = "rgba(251, 191, 36, 0.75)";
    ctx.lineWidth = 8;
    ctx.beginPath();
    ctx.arc(256, 270, ringRadius, 0, Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = "#f97316";
    ctx.beginPath();
    ctx.arc(256, 270, 70, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 34px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("NFC", 256, 282);

    ctx.fillStyle = "#fbbf24";
    ctx.font = "bold 32px system-ui, sans-serif";
    ctx.fillText("Tag Patinha Detectada!", 256, 405);

    ctx.fillStyle = "#e4e4e7";
    ctx.font = "22px system-ui, sans-serif";
    ctx.fillText("Notificando o dono do pet...", 256, 445);
    return;
  }

  // stage === "DETECTED"
  ctx.fillStyle = "#dc2626";
  ctx.beginPath();
  ctx.roundRect(32, 90, 448, 74, 20);
  ctx.fill();

  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 21px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("ATENÇÃO: ANIMAL DESAPARECIDO", 256, 135);

  ctx.fillStyle = "rgba(16, 185, 129, 0.18)";
  ctx.strokeStyle = "#10b981";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.roundRect(32, 182, 448, 64, 16);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = "#34d399";
  ctx.font = "bold 21px system-ui, sans-serif";
  ctx.fillText("✓ Dono Notificado em Tempo Real", 256, 221);

  ctx.fillStyle = "#27272a";
  ctx.beginPath();
  ctx.roundRect(32, 266, 448, 360, 28);
  ctx.fill();

  ctx.fillStyle = "#f97316";
  ctx.beginPath();
  ctx.arc(118, 355, 52, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 42px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("🐾", 118, 370);

  ctx.textAlign = "left";
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 38px system-ui, sans-serif";
  ctx.fillText("Thor", 192, 345);

  ctx.fillStyle = "#fbbf24";
  ctx.font = "bold 22px system-ui, sans-serif";
  ctx.fillText("Golden Retriever • 3 anos", 192, 380);

  ctx.fillStyle = "#18181b";
  ctx.beginPath();
  ctx.roundRect(56, 432, 400, 96, 16);
  ctx.fill();

  ctx.fillStyle = "#f4f4f5";
  ctx.font = "20px system-ui, sans-serif";
  ctx.fillText("“Ele é super dócil! Por favor,", 76, 470);
  ctx.fillText("envie o GPS ou chame no Whats.”", 76, 502);

  ctx.fillStyle = "rgba(251, 191, 36, 0.18)";
  ctx.beginPath();
  ctx.roundRect(56, 544, 400, 58, 14);
  ctx.fill();

  ctx.fillStyle = "#fbbf24";
  ctx.textAlign = "center";
  ctx.font = "bold 21px system-ui, sans-serif";
  ctx.fillText("★ Oferece Recompensa", 256, 580);

  ctx.fillStyle = "#f97316";
  ctx.beginPath();
  ctx.roundRect(32, 654, 448, 86, 22);
  ctx.fill();

  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 25px system-ui, sans-serif";
  ctx.fillText("📍 Enviar Localização GPS", 256, 706);

  ctx.fillStyle = "#10b981";
  ctx.beginPath();
  ctx.roundRect(32, 760, 448, 86, 22);
  ctx.fill();

  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 25px system-ui, sans-serif";
  ctx.fillText("💬 Chamar Tutor no WhatsApp", 256, 812);
}

export function PetCollar3D() {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    const width = container.clientWidth || 500;
    const height = container.clientHeight || 520;

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(36, width / height, 0.1, 100);
    camera.position.set(0, -0.2, 9.8);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.12;

    container.innerHTML = "";
    container.appendChild(renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0xffffff, 1.6);
    scene.add(ambientLight);

    const mainLight = new THREE.DirectionalLight(0xfffbeb, 2.2);
    mainLight.position.set(4, 7, 6);
    scene.add(mainLight);

    const fillLight = new THREE.DirectionalLight(0xfde68a, 1.3);
    fillLight.position.set(-5, 3, 4);
    scene.add(fillLight);

    const backRimLight = new THREE.DirectionalLight(0xf97316, 1.1);
    backRimLight.position.set(0, -4, -3);
    scene.add(backRimLight);

    const rootGroup = new THREE.Group();
    scene.add(rootGroup);

    // =========================================================
    // 1. COLEIRA 3D ESTILIZADA (Mais no alto para a patinha ficar bem pendurada abaixo dela)
    // =========================================================
    const collarGroup = buildStylizedCollar3D();
    collarGroup.position.set(-0.62, 1.72, 0);
    collarGroup.rotation.x = 0.28;
    collarGroup.rotation.z = -0.03;
    rootGroup.add(collarGroup);

    // =========================================================
    // 2. TAG 3D EM FORMATO DE PATINHA PENDURADA ABAIXO DA COLEIRA (Sem atravessar o passador!)
    // =========================================================
    // A base do passador amarelo fica em Y ≈ +0.89 e a argolinha creme desce até Y ≈ +0.62.
    // Posicionamos o pivô da patinha em Y = +0.65, fazendo a orelhinha da patinha engatar
    // na argola creme e o corpo branco da patinha ficar 100% abaixo da coleira!
    const tagPivot = new THREE.Group();
    tagPivot.position.set(-0.86, 0.65, 1.48);
    rootGroup.add(tagPivot);

    const pawTagMeshGroup = buildPawShapedTag3D();
    pawTagMeshGroup.scale.set(0.96, 0.96, 0.96);
    // O furo da orelhinha da patinha está em yLocal = +0.72 * 0.96 = +0.69.
    // Colocando position.y = -0.69, o furo fica exatamente no centro do tagPivot (na argola creme)!
    pawTagMeshGroup.position.set(0, -0.69, 0);
    tagPivot.add(pawTagMeshGroup);

    // Ondas de pulso NFC 3D saindo da almofada central da patinha
    const pulseRings: THREE.Mesh[] = [];
    for (let i = 0; i < 3; i++) {
      const ringGeom = new THREE.RingGeometry(0.42, 0.49, 48);
      const ringMat = new THREE.MeshBasicMaterial({
        color: i % 2 === 0 ? 0xf97316 : 0xfbbf24,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0,
      });
      const ring = new THREE.Mesh(ringGeom, ringMat);
      ring.position.set(0, -0.32, 0.14);
      pawTagMeshGroup.add(ring);
      pulseRings.push(ring);
    }

    // =========================================================
    // 3. SMARTPHONE 3D (Aproxima a PONTA DE CIMA ao lado da patinha, sem atravessar nada!)
    // =========================================================
    const phoneGroup = new THREE.Group();
    rootGroup.add(phoneGroup);

    const phoneShape = new THREE.Shape();
    const pw = 0.62;
    const ph = 1.26;
    const pr = 0.14;
    phoneShape.moveTo(-pw + pr, -ph);
    phoneShape.lineTo(pw - pr, -ph);
    phoneShape.quadraticCurveTo(pw, -ph, pw, -ph + pr);
    phoneShape.lineTo(pw, ph - pr);
    phoneShape.quadraticCurveTo(pw, ph, pw - pr, ph);
    phoneShape.lineTo(-pw + pr, ph);
    phoneShape.quadraticCurveTo(-pw, ph, -pw, ph - pr);
    phoneShape.lineTo(-pw, -ph + pr);
    phoneShape.quadraticCurveTo(-pw, -ph, -pw + pr, -ph);

    const phoneBodyGeom = new THREE.ExtrudeGeometry(phoneShape, {
      depth: 0.09,
      bevelEnabled: true,
      bevelThickness: 0.025,
      bevelSize: 0.022,
      bevelSegments: 5,
    });
    phoneBodyGeom.center();
    const phoneBodyMat = new THREE.MeshStandardMaterial({
      color: 0x18181b,
      metalness: 0.7,
      roughness: 0.25,
    });
    const phoneBody = new THREE.Mesh(phoneBodyGeom, phoneBodyMat);
    phoneGroup.add(phoneBody);

    const screenCanvas = document.createElement("canvas");
    screenCanvas.width = 512;
    screenCanvas.height = 1024;
    const screenCtx = screenCanvas.getContext("2d");

    const screenTexture = new THREE.CanvasTexture(screenCanvas);
    screenTexture.colorSpace = THREE.SRGBColorSpace;

    const screenGeom = new THREE.PlaneGeometry(1.16, 2.4);
    const screenMat = new THREE.MeshBasicMaterial({
      map: screenTexture,
    });
    const screenMesh = new THREE.Mesh(screenGeom, screenMat);
    screenMesh.position.z = 0.076;
    phoneGroup.add(screenMesh);

    let targetRotY = 0;
    let targetRotX = 0;

    const handlePointerMove = (e: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      targetRotY = nx * 0.25;
      targetRotX = ny * 0.12;
    };

    const handlePointerLeave = () => {
      targetRotY = 0;
      targetRotX = 0;
    };

    container.addEventListener("pointermove", handlePointerMove);
    container.addEventListener("pointerleave", handlePointerLeave);

    const handleResize = () => {
      if (!container) return;
      const w = container.clientWidth || 500;
      const h = container.clientHeight || 520;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", handleResize);

    const clock = new THREE.Clock();
    let currentStage: ScanStage = "APPROACHING";
    let animFrameId = 0;

    const animate = () => {
      animFrameId = requestAnimationFrame(animate);
      const elapsed = clock.getElapsedTime();
      const cycleDuration = 8.5;
      const t = ((elapsed % cycleDuration) + cycleDuration) % cycleDuration;

      let nextStage: ScanStage = "APPROACHING";
      if (t >= 2.2 && t < 3.9) {
        nextStage = "SCANNING";
      } else if (t >= 3.9) {
        nextStage = "DETECTED";
      }

      if (nextStage !== currentStage) {
        currentStage = nextStage;
      }

      // Flutuação suave em conjunto da coleira e da patinha
      const floatY = Math.sin(elapsed * 1.6) * 0.035;
      collarGroup.position.y = 1.72 + floatY;
      tagPivot.position.y = 0.65 + floatY;

      // Balanço pendular suave da patinha pendurada na argola
      const swingBoost = nextStage === "SCANNING" ? 0.11 : 0.05;
      tagPivot.rotation.z = Math.sin(elapsed * 3.0) * swingBoost;
      tagPivot.rotation.x = Math.cos(elapsed * 2.2) * 0.03;
      pawTagMeshGroup.rotation.y = Math.sin(elapsed * 1.4) * 0.14 + 0.06;

      // Trajetória do Celular SEM sobrepor nem atravessar a patinha ou a coleira:
      // A patinha ocupa x ∈ [-1.42, -0.26] e y ∈ [-0.62, +0.52].
      // No momento da leitura (SCANNING), o centro do celular fica em x = +0.62, y = -1.32,
      // inclinado (rotation.z = +0.26), fazendo a ponta superior esquerda do celular chegar
      // pertinho da borda inferior-direita da patinha (x ≈ -0.18, y ≈ -0.25), sem nunca cobrir ou cortar a patinha!
      let phoneX = 1.05;
      let phoneY = -1.55;
      let phoneZ = 1.85;
      let phoneRotY = -0.18;
      let phoneRotX = -0.06;
      let phoneRotZ = 0.22;

      if (t < 2.2) {
        // 1. Aproximando a ponta superior do celular em direção à lateral inferior da patinha
        const p = t / 2.2;
        const ease = 1 - (1 - p) ** 3;
        phoneX = THREE.MathUtils.lerp(1.45, 0.58, ease);
        phoneY = THREE.MathUtils.lerp(-1.95, -1.32, ease);
        phoneZ = THREE.MathUtils.lerp(1.95, 1.82, ease);
        phoneRotY = THREE.MathUtils.lerp(-0.28, -0.14, ease);
        phoneRotX = THREE.MathUtils.lerp(-0.1, -0.05, ease);
        phoneRotZ = THREE.MathUtils.lerp(0.12, 0.28, ease);
      } else if (t < 3.9) {
        // 2. Ponta de cima do celular próxima à patinha lendo o NFC (sem tocar nem cobrir a patinha!)
        const p = (t - 2.2) / 1.7;
        phoneX = 0.58 + Math.sin(p * Math.PI) * 0.03;
        phoneY = -1.32 + Math.cos(p * Math.PI * 2) * 0.025;
        phoneZ = 1.82;
        phoneRotY = -0.14;
        phoneRotX = -0.05;
        phoneRotZ = 0.28;
      } else if (t < 7.6) {
        // 3. Sobe levemente ao lado direito da patinha, ficando retinho para exibir o perfil completo
        const p = Math.min((t - 3.9) / 0.95, 1);
        const ease = 1 - (1 - p) ** 3;
        phoneX = THREE.MathUtils.lerp(0.58, 0.92, ease);
        phoneY = THREE.MathUtils.lerp(-1.32, -0.62, ease);
        phoneZ = THREE.MathUtils.lerp(1.82, 1.88, ease);
        phoneRotY = THREE.MathUtils.lerp(-0.14, -0.16, ease);
        phoneRotX = THREE.MathUtils.lerp(-0.05, 0.0, ease);
        phoneRotZ = THREE.MathUtils.lerp(0.28, 0.0, ease);
      } else {
        // 4. Retorno suave para reiniciar o ciclo
        const p = (t - 7.6) / 0.9;
        const ease = p * p * (3 - 2 * p);
        phoneX = THREE.MathUtils.lerp(0.92, 1.45, ease);
        phoneY = THREE.MathUtils.lerp(-0.62, -1.95, ease);
        phoneZ = THREE.MathUtils.lerp(1.88, 1.95, ease);
        phoneRotY = THREE.MathUtils.lerp(-0.16, -0.28, ease);
        phoneRotX = THREE.MathUtils.lerp(0.0, -0.1, ease);
        phoneRotZ = THREE.MathUtils.lerp(0.0, 0.12, ease);
      }

      phoneGroup.position.set(phoneX, phoneY, phoneZ);
      phoneGroup.rotation.set(phoneRotX, phoneRotY, phoneRotZ);

      // Ondas de Pulso NFC saindo da patinha
      pulseRings.forEach((ring, idx) => {
        const mat = ring.material as THREE.MeshBasicMaterial;
        if (t >= 2.0 && t <= 4.5) {
          const localT = (t - 2.0 + idx * 0.35) % 1.2;
          const progress = localT / 1.2;
          const scale = 1 + progress * 1.35;
          ring.scale.set(scale, scale, 1);
          ring.position.z = 0.14 + progress * 0.25;
          mat.opacity = (1 - progress) * 0.85;
        } else {
          mat.opacity = 0;
        }
      });

      if (screenCtx) {
        drawPhoneScreen(screenCtx, currentStage, elapsed);
        screenTexture.needsUpdate = true;
      }

      rootGroup.rotation.y += (targetRotY - rootGroup.rotation.y) * 0.08;
      rootGroup.rotation.x += (targetRotX - rootGroup.rotation.x) * 0.08;

      renderer.render(scene, camera);
    };

    animate();

    return () => {
      cancelAnimationFrame(animFrameId);
      window.removeEventListener("resize", handleResize);
      container.removeEventListener("pointermove", handlePointerMove);
      container.removeEventListener("pointerleave", handlePointerLeave);
      renderer.dispose();
    };
  }, []);

  return (
    <div className="relative w-full max-w-lg mx-auto select-none">
      <div
        ref={mountRef}
        className="w-full h-[460px] sm:h-[520px] cursor-grab active:cursor-grabbing"
        aria-label="Modelo 3D de coleira com tag em formato de patinha NFC pendurada e celular aproximando a ponta superior para ler a tag"
      />
    </div>
  );
}
